import { cn } from "../../lib/utils.ts";
import { IconMinus, IconPlus } from "../brand/icons.tsx";

export interface StepperProps {
  value: number;
  onChange: (next: number) => void;
  min?: number;
  max?: number;
  className?: string;
  /** Accessible label for the control group. */
  "aria-label"?: string;
}

/** Quantity stepper — clamps to [min, max]. Gold "+" leads the eye. */
export function Stepper({
  value,
  onChange,
  min = 0,
  max = Number.POSITIVE_INFINITY,
  className,
  "aria-label": ariaLabel = "Quantity",
}: StepperProps) {
  const dec = () => onChange(Math.max(min, value - 1));
  const inc = () => onChange(Math.min(max, value + 1));

  return (
    // biome-ignore lint/a11y/useSemanticElements: a borderless toolbar group, not a form fieldset
    <div
      className={cn(
        "inline-flex items-center gap-2.5 rounded-full border border-hairline bg-[oklch(100%_0_0_/_5%)] p-1",
        className,
      )}
      role="group"
      aria-label={ariaLabel}
    >
      <button
        type="button"
        onClick={dec}
        disabled={value <= min}
        aria-label="Decrease"
        className="grid size-8 place-items-center rounded-full bg-[oklch(100%_0_0_/_8%)] text-cream transition-colors disabled:opacity-40"
      >
        <IconMinus size={15} />
      </button>
      <span className="min-w-4 text-center font-display font-semibold text-[0.95rem] tabular-nums">
        {value}
      </span>
      <button
        type="button"
        onClick={inc}
        disabled={value >= max}
        aria-label="Increase"
        className="grid size-8 place-items-center rounded-full bg-gold text-primary-foreground transition-colors disabled:opacity-40"
      >
        <IconPlus size={15} />
      </button>
    </div>
  );
}
